import { getClass } from '../shared/classes';
import { ABILITIES } from '../shared/abilities';
import { zoneLevelAt } from '../shared/enemies';
import { BIOMES, biomeAt } from '../shared/biomes';
import { NPC_CHATTER_BUBBLE_MS, PLAYER_RADIUS, QUEST_RANGE, TICK_MS, TRADE_RANGE } from '../shared/constants';
import { nearbyObstacles, pushOut } from '../shared/collision';
import { ITEMS } from '../shared/items';
import { clamp, damp } from '../shared/math';
import type { GameEvent } from '../shared/protocol';
import type { ItemId, NpcInfo } from '../shared/types';
import { Input } from './input';
import { Net } from './net';
import { Renderer } from './render';
import { sfx } from './sfx';
import { UI } from './ui';
import { WorldView } from './view';

const canvas = document.getElementById('game') as HTMLCanvasElement;
const minimap = document.getElementById('minimap') as HTMLCanvasElement;

/** Quick bar order: 1 = health, 2 = mana, 3 = scroll. */
const HOTBAR: Record<number, ItemId> = { 1: 'health', 2: 'mana', 3: 'scroll' };

const renderer = new Renderer(canvas);
const view = new WorldView();
const net = new Net();

let selfAim = -Math.PI / 2;
let inputAccumulator = 0;
let lastLevel = 1;
let deathShown = false;
let lastDeathBy = '';
/** The trader we are currently standing next to, if any. */
let nearNpc: NpcInfo | null = null;


const ui = new UI({
  onPlay: (name, classId) => {
    sfx.unlock();
    ui.showConnecting('Connecting to the world...');
    net.connect(name, classId);
  },
  onSpend: (stat) => {
    net.send({ t: 'spend', stat });
    sfx.play('ui', { volume: 0.5 });
  },
  onChatSend: (text) => {
    // "/p " (or "/party ") sends to your party only.
    const party = /^\/(?:p|party)\s+(.*)$/i.exec(text);
    if (party) {
      if (party[1].trim().length > 0) net.send({ t: 'chat', text: party[1], party: true });
      return;
    }
    net.send({ t: 'chat', text });
  },
  onChatOpenChange: (open) => {
    input.chatOpen = open;
    input.keys.clear();
    syncInput();
    if (!open) input.suppressClick();
  },
  onBuy: (item, qty) => {
    net.send({ t: 'buy', item, qty });
    sfx.play('ui', { volume: 0.6 });
  },
  onUse: (item) => net.send({ t: 'use', item }),
  onEquip: (uid) => {
    net.send({ t: 'equip', uid });
    sfx.play('ui', { volume: 0.5 });
  },
  onUnequip: (slot) => {
    net.send({ t: 'unequip', slot });
    sfx.play('ui', { volume: 0.5 });
  },
  onSell: (uid) => {
    net.send({ t: 'sell', uid });
    sfx.play('ui', { volume: 0.5 });
  },
  onBuyGear: (uid) => {
    net.send({ t: 'buyGear', uid });
    sfx.play('ui', { volume: 0.6 });
  },
  onUpgrade: (uid) => {
    net.send({ t: 'upgrade', uid });
    sfx.play('ui', { volume: 0.6 });
  },
  onAbility: (slot) => {
    if (!net.connected) return;
    const me = view.me;
    if (!me || me.snap.state !== 'alive') return;
    net.send({ t: 'ability', slot, aim: selfAim });
  },
  onAcceptQuest: (id) => {
    net.send({ t: 'acceptQuest', id });
    sfx.play('ui', { volume: 0.6 });
  },
  onAbandonQuest: (id) => net.send({ t: 'abandonQuest', id }),
  onPartyInvite: (name) => net.send({ t: 'partyInvite', name }),
  onPartyAccept: (name) => net.send({ t: 'partyAccept', name }),
  onPartyDecline: (name) => net.send({ t: 'partyDecline', name }),
  onPartyLeave: () => net.send({ t: 'partyLeave' }),
  onPartyKick: (name) => net.send({ t: 'partyKick', name }),
  onFriendAdd: (name) => net.send({ t: 'friendAdd', name }),
  onFriendRemove: (name) => net.send({ t: 'friendRemove', name }),
  onResume: () => {
    ui.closePause();
    syncInput();
  },
  onLogout: () => {
    // Leaving the world: close the menu and hand input back to the login screen.
    ui.closePause();
    syncInput();
    // The server saves the character on disconnect, then we go back to picking one.
    net.logout();
  },
});
/**
 * The single place that decides whether the player can move. `enabled` is
 * derived from `active` (are we even in the world) plus whatever UI is up, so
 * the flags cannot drift apart - which is exactly how returning to character
 * select used to leave you unable to move.
 */
function syncInput(): void {
  input.enabled = input.active && !ui.chatOpen && !ui.pauseOpen;
  if (!input.enabled) input.keys.clear();
}

const input = new Input(canvas, {
  onToggleChat: () => {
    if (!net.connected) return;
    ui.toggleChat();
  },
  onToggleChar: () => {
    ui.toggleCharPanel();
    sfx.play('ui', { volume: 0.4 });
  },
  onToggleGear: () => {
    ui.toggleGearPanel();
    sfx.play('ui', { volume: 0.4 });
  },
  onToggleMute: () => {
    const enabled = sfx.toggle();
    ui.addToast(enabled ? 'Sound on' : 'Sound muted');
  },
  onEscape: () => {
    if (ui.chatOpen) ui.closeChat();
    else if (ui.shopOpen) ui.closeShop();
    else if (ui.questPanelOpen) ui.closeQuestPanel();
    else if (ui.socialPanelOpen) ui.closeSocialPanel();
    else if (ui.charPanelOpen) ui.closeCharPanel();
    else if (ui.gearPanelOpen) ui.closeGearPanel();
    else if (net.status === 'playing') {
      ui.togglePause();
      syncInput();
    }
  },
  onHotbar: (slot) => {
    const item = HOTBAR[slot];
    if (item) net.send({ t: 'use', item });
  },
  onInteract: () => {
    if (!nearNpc) return;
    if (nearNpc.role === 'trader' || nearNpc.role === 'weaponeer' || nearNpc.role === 'armourer') {
      ui.toggleShop(nearNpc);
    } else if (nearNpc.role === 'questgiver') ui.toggleQuestPanel(nearNpc);
    else return;
    sfx.play('ui', { volume: 0.5 });
  },
  onToggleSocial: () => {
    ui.toggleSocialPanel();
    sfx.play('ui', { volume: 0.4 });
  },
  onTogglePerf: () => ui.togglePerf(),
  onToggleQuality: () => {
    const low = renderer.toggleQuality();
    ui.addToast(low ? 'Low graphics mode on' : 'Graphics back to full');
  },
  onAbility: (slot) => {
    if (!net.connected) return;
    const me = view.me;
    if (!me || me.snap.state !== 'alive') return;
    net.send({ t: 'ability', slot, aim: selfAim });
  },
  onFirstGesture: () => sfx.unlock(),
});

/* ------------------------------------------------------------------ networking */

net.onWelcome = (welcome) => {
  view.reset();
  view.setWorld(welcome.world);
  view.selfId = welcome.id;
  view.applyPlayers(welcome.players);
  view.applyEnemies(welcome.enemies);
  view.applyProjectiles(welcome.projectiles);
  view.applyDrops(welcome.drops);
  view.leaderboard = welcome.leaderboard;
  renderer.buildMinimapBase(welcome.world, view.obstacles);
  nearNpc = null;
  ui.setLoadout({}, [], [], []);
  ui.setLandmarks(view.landmarks);

  const me = view.me;
  if (me) {
    selfAim = me.aim;
    renderer.follow(me.x, me.y, 0, true);
    lastLevel = me.snap.level;
    ui.setClass(me.snap.cls);
  }

  ui.clearChat();
  for (const line of welcome.chat) ui.pushChat(line);
  ui.setOnline(welcome.players.length);
  ui.hideLogin();
  ui.clearError();
  input.active = true;
  input.keys.clear();
  syncInput();
  ui.addToast('Welcome to the world. Good luck out there.');
};

net.onError = (message) => {
  ui.hideConnecting();
  ui.setError(message);
  ui.showLogin();
  input.active = false;
  syncInput();
};

net.onLeaderboard = (entries) => {
  // Carved straight onto the monument; there is no panel to update.
  view.leaderboard = entries;
};

net.onNotice = (message, kind) => {
  ui.addToast(message, kind);
};

net.onLoadout = (msg) => {
  ui.setLoadout(msg.equipment, msg.inventory, msg.shopWeapons, msg.shopArmour);
};

net.onSocial = (state) => {
  ui.setSocial(state);
  // Keep the renderer's idea of who is grouped with us in step.
  view.partyIds = new Set((state.party?.members ?? []).map((m) => m.id));
};

net.onClosed = () => {
  ui.hideConnecting();
  ui.closeChat();
  ui.closeCharPanel();
  ui.closeGearPanel();
  ui.setLoadout({}, [], [], []);
  ui.hideDeath();
  ui.showLogin();
  input.active = false;
  input.keys.clear();
  syncInput();
  // Logging out deliberately is not an error - just refresh the roster so the
  // character we were playing is there to pick again.
  if (net.intentionalClose) {
    ui.clearError();
    void ui.loadRoster();
  } else {
    ui.setError('Disconnected from the server. Reload the page to try again.');
  }
};

/* ----------------------------------------------------------------- game loop */

function handleEvents(events: GameEvent[]): void {
  const me = view.me;
  const px = me?.x ?? 0;
  const py = me?.y ?? 0;

  for (const event of events) {
    const dx = event.x - px;
    const dy = event.y - py;
    const distance = Math.hypot(dx, dy);
    const volume = clamp(1 - distance / 1500, 0.06, 1);
    const pan = clamp(dx / 800, -1, 1);

    switch (event.k) {
      case 'hit': {
        const colour = event.on === 'player' ? '#ff9088' : event.crit ? '#ffd166' : '#f4f8ff';
        renderer.damageText(event.x, event.y, event.crit ? `${event.amount}!` : String(event.amount), colour, event.crit ? 21 : 15);
        renderer.burst(event.x, event.y, event.on === 'player' ? '#ff6b5a' : '#ffe0a8', event.crit ? 11 : 6, {
          speed: 150,
          size: 3.2,
          life: 0.35,
          glow: true,
        });
        if (event.on === 'enemy') view.flashEnemy(event.id);
        else view.flashPlayer(event.id);
        if (event.on === 'player') {
          sfx.play('hurt', { pan, volume: volume * 0.9 });
          renderer.shakeCamera(5);
        } else {
          sfx.play(event.crit ? 'crit' : 'hit', { pan, volume: volume * 0.55 });
        }
        break;
      }
      case 'kill': {
        const hostile = event.on === 'enemy';
        if (!hostile && event.id === view.selfId) lastDeathBy = event.by;
        renderer.burst(event.x, event.y, hostile ? '#ffb262' : '#8fb6ff', hostile ? 26 : 30, {
          speed: 210,
          size: 4,
          life: 0.7,
          glow: true,
        });
        renderer.ring(event.x, event.y, 6, hostile ? 64 : 88, hostile ? '#ffc98a' : '#9fc4ff', 0.5, 4);
        if (hostile) {
          renderer.damageText(event.x, event.y - 16, event.name, '#ffd9a0', 13);
          sfx.play('kill', { pan, volume: volume * 0.7 });
        } else {
          renderer.shakeCamera(10);
          sfx.play('death', { pan, volume: volume * 0.9 });
        }
        break;
      }
      case 'levelup': {
        renderer.ring(event.x, event.y, 10, 130, '#ffd977', 0.9, 6);
        renderer.burst(event.x, event.y, '#ffe9a8', 34, { speed: 260, size: 4.4, life: 1, glow: true });
        renderer.damageText(event.x, event.y - 30, 'LEVEL UP!', '#ffe9a8', 22);
        sfx.play('levelup', { pan, volume: volume * 0.8 });
        break;
      }
      case 'xp': {
        if (distance < 120) renderer.damageText(event.x, event.y, `+${event.amount} xp`, '#a8e06a', 12);
        break;
      }
      case 'swing': {
        const cls = getClass(event.cls);
        const colour = cls.attack.kind === 'melee' ? cls.accent : cls.color;
        renderer.slash(event.x, event.y, event.aim, event.arc, event.range, colour);
        if (cls.attack.kind === 'melee') {
          renderer.burst(
            event.x + Math.cos(event.aim) * event.range * 0.7,
            event.y + Math.sin(event.aim) * event.range * 0.7,
            cls.accent,
            4,
            { speed: 90, size: 2.4, life: 0.22, aim: event.aim, spread: event.arc },
          );
        }
        sfx.play(cls.attack.kind === 'melee' ? 'swing' : 'shoot', { pan, volume: volume * 0.5 });
        break;
      }
      case 'spawn': {
        renderer.ring(event.x, event.y, 4, event.radius, event.color, 0.4, 2.5);
        if (distance < 900) sfx.play('spawn', { pan, volume: volume * 0.25 });
        break;
      }
      case 'heal': {
        if (event.id === view.selfId) {
          renderer.damageText(event.x, event.y - 18, `+${event.amount}`, '#7ee08a', 16);
          sfx.play('spawn', { pan, volume: volume * 0.5, detune: 0.3 });
        }
        renderer.burst(event.x, event.y, '#7ee08a', 14, { speed: 130, size: 3, life: 0.55, glow: true });
        break;
      }
      case 'mana': {
        if (event.id === view.selfId) {
          renderer.damageText(event.x, event.y - 18, `+${event.amount}`, '#7fb4ff', 16);
          sfx.play('spawn', { pan, volume: volume * 0.5, detune: 0.5 });
        }
        renderer.burst(event.x, event.y, '#6aa8ff', 14, { speed: 130, size: 3, life: 0.55, glow: true });
        break;
      }
      case 'loot': {
        const mine = event.id === view.selfId;
        const colour =
          event.kind === 'coin'
            ? '#f2c94c'
            : event.kind === 'gear'
              ? (event.color ?? '#9aa4b2')
              : ITEMS[event.kind].color;
        if (mine) {
          const label =
            event.kind === 'coin'
              ? `+${event.amount} c`
              : event.kind === 'gear'
                ? (event.label ?? 'Gear')
                : `+${ITEMS[event.kind].name}`;
          renderer.damageText(event.x, event.y - 14, label, colour, 13);
          sfx.play('ui', { pan, volume: volume * 0.45, detune: 0.25 });
        }
        const sparkle = event.kind === 'coin' ? '#ffe08a' : colour;
        renderer.burst(event.x, event.y, sparkle, mine ? 10 : 5, {
          speed: 120,
          size: 2.6,
          life: 0.4,
          glow: true,
        });
        break;
      }
      case 'cast': {
        const def = ABILITIES[event.ability];
        const colour = def?.colour ?? '#ffffff';
        const mine = event.id === view.selfId;
        renderer.castEffect(event.x, event.y, event.aim, event.radius, event.distance, colour, event.ability);

        if (event.ability === 'charge' || event.ability === 'blink') {
          // Trail between where the caster started and where they ended up.
          renderer.ring(event.x, event.y, 6, 46, colour, 0.42, 3);
          renderer.burst(event.x, event.y, colour, 20, { speed: 200, size: 3.4, life: 0.5, glow: true });
        }
        if (mine) {
          sfx.play(event.ability === 'blink' ? 'spawn' : 'kill', { pan, volume: volume * 0.7, detune: 0.15 });
          renderer.shakeCamera(event.ability === 'nova' || event.ability === 'whirlwind' ? 6 : 3);
        } else {
          sfx.play('hit', { pan, volume: volume * 0.4 });
        }
        break;
      }
      case 'say': {
        renderer.say(event.id, event.text, NPC_CHATTER_BUBBLE_MS / 1000);
        if (distance < 1400) sfx.play('chat', { pan, volume: volume * 0.4 });
        break;
      }
      case 'quest': {        const mine = event.id === view.selfId;
        if (event.phase === 'accept') {
          if (mine) {
            ui.addToast(`Bounty accepted: ${event.name} (0/${event.count}).`);
            sfx.play('ui', { pan, volume: 0.5 });
          }
          break;
        }
        // Completion: a green flourish plus the payout spelled out.
        renderer.ring(event.x, event.y, 12, 150, '#b8f0a0', 0.9, 6);
        renderer.burst(event.x, event.y, '#b8f0a0', 32, { speed: 280, size: 4, life: 0.9, glow: true });
        renderer.damageText(event.x, event.y - 38, 'BOUNTY COMPLETE', '#c8f0b8', 18);
        renderer.damageText(event.x, event.y - 14, `+${event.xpReward} xp   +${event.coinReward} coins`, '#f0d79a', 14);
        if (mine) {
          ui.addToast(`${event.name} complete: +${event.xpReward} xp, +${event.coinReward} coins.`, 'level');
          sfx.play('levelup', { pan, volume: volume * 0.8 });
        } else {
          sfx.play('ui', { pan, volume: volume * 0.4 });
        }
        break;
      }
      default:
        break;
    }
  }
}

function applyMessages(): void {
  for (const message of net.drain()) {
    if (message.t === 'state') {
      view.applyPlayers(message.players);
      view.applyEnemies(message.enemies);
      view.applyProjectiles(message.projectiles);
      view.applyDrops(message.drops);
      for (const line of message.chat) {
        ui.pushChat(line);
        if (!line.system && line.from !== view.me?.snap.name) sfx.play('chat', { volume: 0.3 });
      }
      handleEvents(message.events);
    }
  }
}

function updateSelf(dt: number): void {
  const me = view.me;
  const world = view.world;
  if (!me || !world) return;

  const mouseWorld = renderer.screenToWorld(input.mouseX, input.mouseY);
  selfAim = Math.atan2(mouseWorld.y - me.y, mouseWorld.x - me.x);
  me.aim = selfAim;

  if (me.snap.state !== 'alive') {
    me.x = me.snap.x;
    me.y = me.snap.y;
    me.moving = 0;
    return;
  }

  let dx = 0;
  let dy = 0;
  if (input.left) dx -= 1;
  if (input.right) dx += 1;
  if (input.up) dy -= 1;
  if (input.down) dy += 1;

  if (dx !== 0 || dy !== 0) {
    const len = Math.hypot(dx, dy);
    const step = me.snap.speed * dt;
    me.x += (dx / len) * step;
    me.y += (dy / len) * step;
    me.moving = Math.min(1, me.moving + dt * 7);
  } else {
    me.moving = Math.max(0, me.moving - dt * 7);
  }

  const fixed = pushOut(me.x, me.y, PLAYER_RADIUS, nearbyObstacles(view.obstacles, me.x, me.y, PLAYER_RADIUS + 60));
  me.x = clamp(fixed.x, PLAYER_RADIUS, world.width - PLAYER_RADIUS);
  me.y = clamp(fixed.y, PLAYER_RADIUS, world.height - PLAYER_RADIUS);

  // Reconcile the prediction with the authoritative position.
  const error = Math.hypot(me.snap.x - me.x, me.snap.y - me.y);
  if (error > 220) {
    me.x = me.snap.x;
    me.y = me.snap.y;
  } else if (error > 1) {
    const k = damp(11, dt);
    me.x += (me.snap.x - me.x) * k;
    me.y += (me.snap.y - me.y) * k;
  }

  inputAccumulator += dt;
  if (inputAccumulator >= TICK_MS / 1000) {
    inputAccumulator = 0;
    net.send({ t: 'input', input: input.snapshot(selfAim) });
  }
}

function updateTrader(): void {
  const me = view.me;
  const world = view.world;
  if (!me || !world) {
    nearNpc = null;
    return;
  }
  let closest: NpcInfo | null = null;
  let best = Infinity;
  for (const npc of world.npcs) {
    // Guards and the monument are scenery - nothing to interact with.
    if (npc.role === 'guard' || npc.role === 'monument') continue;
    const range = npc.role === 'questgiver' ? QUEST_RANGE : TRADE_RANGE;
    const dx = npc.x - me.x;
    const dy = npc.y - me.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < range * range && d2 < best) {
      best = d2;
      closest = npc;
    }
  }
  const changed = (closest?.id ?? null) !== (nearNpc?.id ?? null);
  nearNpc = closest;
  if (changed) ui.setTradePrompt(closest);
  // Walking out of reach closes the panel rather than leaving dead buttons up.
  if (!closest) {
    if (ui.shopOpen) {
      ui.closeShop();
      ui.addToast('You step away from the trader.');
    }
    if (ui.questPanelOpen) {
      ui.closeQuestPanel();
      ui.addToast('You step away from the bounty board.');
    }
  }
}

function updateHud(): void {
  const me = view.me;
  if (!me) return;
  const snap = me.snap;

  ui.updateHud(snap);
  ui.setOnline(net.online);
  ui.setPing(net.ping);
  ui.setZone(zoneLevelAt(me.x, me.y), snap.level, snap.inSafeZone, BIOMES[biomeAt(me.x, me.y)].name);

  // Point the way on any bounty that sends us to a landmark.
  const navQuest = snap.quests.find((q) => q.targetX !== undefined && q.targetY !== undefined);
  view.navTarget =
    navQuest && navQuest.targetX !== undefined && navQuest.targetY !== undefined
      ? { x: navQuest.targetX, y: navQuest.targetY, name: navQuest.targetName ?? 'the landmark' }
      : null;

  if (snap.level > lastLevel) {
    lastLevel = snap.level;
    ui.showLevelUp(snap.level);
    ui.addToast(`Level ${snap.level}! You have ${snap.points} points to spend (press C).`, 'level');
  }

  if (snap.state === 'dead') {
    if (!deathShown) {
      deathShown = true;
      ui.showDeath(lastDeathBy);
      ui.closeCharPanel();
    }
    ui.updateDeathTimer(snap.respawnIn);
  } else if (deathShown) {
    deathShown = false;
    ui.hideDeath();
  }
}

let last = performance.now();

/* --- frame rate sampling, so a "low fps" report can be diagnosed in place --- */
let perfFrames = 0;
let perfElapsed = 0;
let perfWorst = 0;
/** Time spent inside the canvas draw calls, and inside the whole frame step. */
let perfDrawTotal = 0;
let perfStepTotal = 0;
/** Cooldown so the adaptive resolution cannot oscillate frame to frame. */
let lastScaleChange = 0;

function trackPerf(rawSeconds: number): void {
  // Clamp absurd gaps (tab was hidden) so they do not skew the average.
  if (rawSeconds > 0 && rawSeconds < 1) {
    perfFrames++;
    perfElapsed += rawSeconds;
    perfWorst = Math.max(perfWorst, rawSeconds);
  }
  if (perfElapsed < 0.5) return;

  const frames = Math.max(1, perfFrames);
  const frameMs = (perfElapsed / frames) * 1000;
  const stepMs = perfStepTotal / frames;

  // Adaptive resolution: shrink the backing store only when *our* frame work is
  // what is slow. It deliberately ignores total frame time, because when the
  // browser's compositor is the bottleneck (Firefox especially) downscaling
  // just adds an upscale to the composite and makes things worse, not better.
  const now = performance.now();
  if (now - lastScaleChange > 1000) {
    if (stepMs > 9 && renderer.renderScale > 0.6) {
      renderer.setScale(renderer.renderScale - 0.1);
      lastScaleChange = now;
    } else if (stepMs < 4 && renderer.renderScale < 1) {
      renderer.setScale(renderer.renderScale + 0.05);
      lastScaleChange = now;
    }
  }

  ui.setPerf({
    fps: frames / perfElapsed,
    frameMs,
    worstMs: perfWorst * 1000,
    drawMs: perfDrawTotal / frames,
    stepMs: perfStepTotal / frames,
    enemies: view.enemies.size,
    players: view.players.size,
    projectiles: view.projectiles.size,
    drops: view.drops.size,
    obstacles: view.obstacles.length,
    zoom: renderer.camera.zoom,
    canvasWidth: renderer.canvasWidth,
    canvasHeight: renderer.canvasHeight,
    dpr: renderer.pixelRatio,
    lowQuality: renderer.lowQuality,
    scale: renderer.renderScale,
  });
  perfFrames = 0;
  perfElapsed = 0;
  perfWorst = 0;
  perfDrawTotal = 0;
  perfStepTotal = 0;
}

/** One frame of the game loop, decoupled from requestAnimationFrame so it can
 *  be driven manually for testing. */
function step(dt: number): void {
  const stepStart = performance.now();
  applyMessages();

  if (net.status === 'playing' && view.world) {
    updateSelf(dt);
    view.update(dt);
    updateTrader();
    renderer.camera.zoom = input.zoom;
    renderer.updateEffects(dt);

    const me = view.me;
    if (me) {
      const look = 70;
      renderer.follow(me.x + Math.cos(selfAim) * look, me.y + Math.sin(selfAim) * look, dt);
    }

    const target = me ? view.nearestEnemy(me.x, me.y, 520) : null;
    const drawStart = performance.now();
    renderer.draw(view, dt, {
      selfId: view.selfId,
      zoom: input.zoom,
      mouse: { x: input.mouseX, y: input.mouseY },
      target: target ? { x: target.x, y: target.y } : null,
      connecting: false,
    });
    renderer.drawMinimap(minimap, view);
    perfDrawTotal += performance.now() - drawStart;

    updateHud();
  } else {
    renderer.updateEffects(dt);
    if (net.status === 'idle' || net.status === 'connecting') ui.drawPreviews(performance.now() / 1000);
  }
  perfStepTotal += performance.now() - stepStart;
}

function frame(now: number): void {
  const raw = (now - last) / 1000;
  last = now;
  const dt = Math.min(0.05, Math.max(0, raw));
  trackPerf(raw);
  step(dt);
  requestAnimationFrame(frame);
}

// Debug handle - handy from the browser console and for automated checks.
(window as unknown as { simplemmo: unknown }).simplemmo = { net, view, ui, input, renderer, step };

window.addEventListener('resize', () => renderer.resize());
window.addEventListener('beforeunload', () => net.disconnect());

// Ask the server which characters already exist so the login screen can offer
// them before the player types anything.
void ui.loadRoster();

// Stop moving the moment the tab is hidden; the server also times out stale input.
document.addEventListener('visibilitychange', () => {
  if (document.hidden && net.connected) {
    net.send({ t: 'input', input: { up: false, down: false, left: false, right: false, aim: selfAim, attack: false } });
  }
});

requestAnimationFrame(frame);
